import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Play, Pause, X, Mic } from "lucide-react";
import { subscribeVoice, toggleVoiceNote, stopVoiceNote, seekVoiceNote, fmtSecs, getVoiceSpeed, setVoiceRate } from "../lib/voicePlayer";

// WhatsApp-style bar at the top: the voice note keeps playing after leaving the chat.
export const VoiceMiniPlayer = () => {
  const [s, setS] = useState(null);
  const [speed, setSpeed] = useState(getVoiceSpeed);
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => subscribeVoice(setS), []);
  const inThisChat = !!s?.convId && location.pathname === `/chat/${s.convId}`;
  if (!s?.id || (inThisChat && s.inView)) return null;
  const pct = s.dur > 0 ? Math.min(100, (s.cur / s.dur) * 100) : 0;
  const cycleSpeed = (e) => {
    e.stopPropagation();
    const next = speed === 1 ? 1.5 : speed === 1.5 ? 2 : 1;
    setSpeed(next);
    try { localStorage.setItem("osc_voice_speed", String(next)); } catch (err) {}
    setVoiceRate(next);
  };
  return (
    <div className="shrink-0 z-40 border-b bg-card shadow-sm" data-testid="voice-mini-player">
      <div className="flex items-center gap-2 px-3 py-1.5">
        <button
          type="button"
          data-testid="voice-mini-toggle"
          onClick={toggleVoiceNote}
          className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 active:scale-95 transition-transform"
        >
          {s.loading ? <span className="h-3.5 w-3.5 rounded-full border-2 border-current border-t-transparent animate-spin" />
            : s.playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
        </button>
        <button
          type="button"
          data-testid="voice-mini-open"
          onClick={() => {
            if (inThisChat) window.dispatchEvent(new Event("osc-voice-scroll-to"));
            else if (s.convId) navigate(`/chat/${s.convId}`);
          }}
          className="flex-1 min-w-0 text-left"
        >
          <div className="flex items-center gap-1 text-xs font-semibold truncate">
            <Mic className="h-3 w-3 text-primary shrink-0" />
            <span className="truncate">{s.sender}</span>
            <span className="text-muted-foreground font-normal tabular-nums ml-auto pl-2 shrink-0">{fmtSecs(s.cur)} / {fmtSecs(s.dur)}</span>
          </div>
          <div
            className="relative h-1 mt-1 rounded-full bg-muted-foreground/25 cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              const r = e.currentTarget.getBoundingClientRect();
              seekVoiceNote((e.clientX - r.left) / r.width);
            }}
          >
            <div className="absolute inset-y-0 left-0 rounded-full bg-primary" style={{ width: `${pct}%` }} />
          </div>
        </button>
        <button type="button" onClick={cycleSpeed} data-testid="voice-mini-speed" className="shrink-0 h-7 min-w-[38px] px-1.5 rounded-full border bg-card text-[11px] font-bold tabular-nums text-primary">
          {speed}x
        </button>
        <button type="button" onClick={stopVoiceNote} data-testid="voice-mini-close" className="h-8 w-8 flex items-center justify-center rounded-full text-muted-foreground hover:bg-muted shrink-0">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
